import { isCancel } from 'axios'
import { defineStore } from 'pinia'
import { computed, h, ref } from 'vue'
import { Slide, toast } from 'vue3-toastify'

import UserInboxToast from '@/components/inbox/UserInboxToast.vue'
import { isUserLoggedIn } from '@/router/utils'
import { fireFundsReceivedConfetti } from '@/utils/fundsReceivedConfetti'
import {
  type InboxCategoryTab,
  inboxBellUnreadCount,
  inboxNotificationBody,
  inboxNotificationPrimaryAction,
  inboxNotificationTitle,
  inboxRowMatchesCategoryTab,
} from '@/utils/inboxNotificationDisplay'
import axios from '@axios'

export interface ProgramAuditApplyFinishedDetail {
  program_uuid: string
  audit_uuid: string
  automation_event_type?: string | null
}

export interface BulkMedicationUpdateFinishedDetail {
  program_uuid?: string
  patient_case_uuid?: string
  bulk_medication_update_uuid?: string
  bulk_patient_case_medication_update_uuid?: string
  automation_event_type?: string | null
}

/** JSON `data` column for automation in-app notifications (v1 contract). */
export interface AutomationInAppNotificationData {
  schema_version?: number
  title?: string
  body?: string
  delivery_mode?: 'interrupt' | 'passive' | 'digest_eligible'

  /** Echoed on merged passive completion payloads; also on `DatabaseNotification.taskable_type`. */
  taskable_type?: string
  opportunity_uuid?: string

  /** Grouped passive completion notifications (automation_completed). */
  opportunity_uuids?: string[]
  completion_count?: number
  task_label?: string
  task_uuid?: string
  program_uuid?: string
  audit_uuid?: string
  program_name?: string
  patient_name?: string
  source_service?: string
  reason_human?: string
  reason_code?: string
  error_json?: unknown
}

export interface ProfileNotificationRow {
  id: string
  type: string
  data: AutomationInAppNotificationData
  read_at: string | null
  created_at: string
  updated_at?: string
  alertable?: boolean
  automation_event_type?: string | null
  task_id?: number | null
  opportunity_id?: number | null

  /** DB column; passive `automation_completed` rows merge per this key (see server notifier). */
  taskable_type?: string | null
  dedupe_key?: string | null

  /** Raw decrypted values from `opportunity.patient` (not stored in `data`); format in UI. */
  patient_first_name?: string | null
  patient_last_name?: string | null
}

const TOASTS_MUTED_STORAGE_KEY = 'vector-user-inbox-toasts-muted'

/** If the API ever returns duplicate ids, keep the last occurrence (newest payload) per id. */
function dedupeInboxItemsById(rows: ProfileNotificationRow[]): ProfileNotificationRow[] {
  const lastById = new Map<string, ProfileNotificationRow>()
  for (const row of rows)
    lastById.set(row.id, row)

  return rows.filter(row => lastById.get(row.id) === row)
}

function inboxRowSortTimestamp(row: ProfileNotificationRow): number {
  const isPassiveCompleted = row.automation_event_type === 'automation_completed' && row.alertable === false
  const iso = isPassiveCompleted ? (row.updated_at ?? row.created_at) : row.created_at
  const ts = Date.parse(iso)

  return Number.isNaN(ts) ? 0 : ts
}

function sortInboxItemsByRecency(rows: ProfileNotificationRow[]): ProfileNotificationRow[] {
  return [...rows].sort((a, b) => inboxRowSortTimestamp(b) - inboxRowSortTimestamp(a))
}

function loadToastsMutedFromStorage(): boolean {
  if (typeof localStorage === 'undefined')
    return false
  try {
    const raw = localStorage.getItem(TOASTS_MUTED_STORAGE_KEY)
    if (raw === null)
      return false

    // VueUse useStorage historically wrote JSON booleans; accept plain strings too
    if (raw === 'true')
      return true
    if (raw === 'false')
      return false

    return JSON.parse(raw) === true
  } catch {
    return false
  }
}

async function safeMarkReadById(markRead: (id: string) => Promise<void>, id: string): Promise<void> {
  try {
    await markRead(id)
  } catch {
    // Keep toast UX resilient if mark-read API fails.
  }
}

/** Exported for UI toggles; mutates the same key the store reads on init. */
export function persistUserInboxToastsMuted(muted: boolean): void {
  try {
    localStorage.setItem(TOASTS_MUTED_STORAGE_KEY, JSON.stringify(muted))
  } catch {
    // private mode / quota
  }
}

export const useUserInboxStore = defineStore('userInbox', () => {
  const items = ref<ProfileNotificationRow[]>([])
  const loading = ref(false)
  const drawerOpen = ref(false)

  /** When true, realtime inbox refetches still run but popup toasts are not shown. */
  const toastsMuted = ref(loadToastsMutedFromStorage())

  /** Bell badge: unread alertables per id; unread passives per merge group. */
  const unreadCount = computed(() => inboxBellUnreadCount(items.value))

  async function fetchList(): Promise<void> {
    if (!isUserLoggedIn()) {
      items.value = []

      return
    }

    loading.value = true
    try {
      const { data } = await axios.get<ProfileNotificationRow[]>('/profile/notifications')

      if (!isUserLoggedIn()) {
        items.value = []

        return
      }

      items.value = sortInboxItemsByRecency(
        dedupeInboxItemsById(Array.isArray(data) ? data : []),
      )
    } catch (error) {
      if (isCancel(error))
        return

      items.value = []
    } finally {
      loading.value = false
    }
  }

  function showRealtimeToast(row: ProfileNotificationRow): void {
    if (!isUserLoggedIn())
      return

    // Read storage here: Pinia proxy assignment on `inbox.toastsMuted` may not update this ref reliably.
    if (loadToastsMutedFromStorage())
      return

    const title = inboxNotificationTitle(row)
    const body = inboxNotificationBody(row)

    const action = inboxNotificationPrimaryAction(row)

    toast(({ closeToast }: { closeToast?: () => void }) => h(UserInboxToast, {
      title,
      body: body || undefined,
      actionLabel: action?.label,
      actionTo: action?.to,
      closeToast,
      onActionClick: () => {
        void safeMarkReadById(markRead, row.id)
      },
    }), {
      toastId: `user-inbox-${row.id}`,
      position: toast.POSITION.TOP_RIGHT,
      transition: Slide,
      closeButton: true,
      closeOnClick: false,
      pauseOnHover: true,
      autoClose: 9000,
      progressStyle: {
        background: 'rgb(var(--v-theme-primary))',
      },
    })
  }

  /**
   * Refetches inbox after `inbox.updated` and shows toasts for newly arrived unread rows.
   * Program UI hints use the separate `resource.updated` websocket event (`resourceUpdatedBroadcast.ts`).
   */
  async function fetchListFromRealtimeSignal(): Promise<void> {
    if (!isUserLoggedIn())
      return

    toastsMuted.value = loadToastsMutedFromStorage()

    const previousIds = new Set(items.value.map(n => n.id))

    await fetchList()

    if (!isUserLoggedIn())
      return

    const newlyArrivedUnread = items.value.filter(
      n => n.read_at == null && !previousIds.has(n.id),
    )

    for (const row of newlyArrivedUnread) {
      if (row.alertable !== false) {
        showRealtimeToast(row)
        if (row.automation_event_type === 'funds_received' && !loadToastsMutedFromStorage())
          void fireFundsReceivedConfetti()
      }
    }
  }

  async function markRead(id: string): Promise<void> {
    await axios.patch(`/profile/notifications/${id}/read`)

    const row = items.value.find(n => n.id === id)
    if (row)
      row.read_at = new Date().toISOString()
  }

  /** Marks multiple rows read (e.g. after user sees them in the inbox drawer). */
  async function markManyRead(ids: string[]): Promise<void> {
    if (ids.length === 0)
      return
    try {
      await axios.post('/profile/notifications/read-selected', { ids })

      const now = new Date().toISOString()
      for (const row of items.value) {
        if (ids.includes(row.id))
          row.read_at = now
      }
    } catch {
      // Keep local state; user can reopen drawer to retry
    }
  }

  async function remove(id: string): Promise<void> {
    await axios.delete(`/profile/notifications/${id}`)
    items.value = items.value.filter(n => n.id !== id)
  }

  async function clearAll(category: InboxCategoryTab = 'all'): Promise<void> {
    loading.value = true
    try {
      await axios.delete('/profile/notifications', {
        params: { category },
      })
      if (category === 'all')
        items.value = []
      else
        items.value = items.value.filter(n => !inboxRowMatchesCategoryTab(n, category))
    } catch {
      // Keep local list; user can retry
    } finally {
      loading.value = false
    }
  }

  function openDrawer(): void {
    drawerOpen.value = true
    void fetchList()
  }

  function closeDrawer(): void {
    drawerOpen.value = false
  }

  function clearLocal(dismissToasts = false): void {
    items.value = []
    if (dismissToasts)
      toast.remove()
  }

  function setToastsMuted(muted: boolean): void {
    const next = Boolean(muted)

    toastsMuted.value = next
    persistUserInboxToastsMuted(next)
  }

  return {
    items,
    loading,
    drawerOpen,
    toastsMuted,
    unreadCount,
    fetchList,
    fetchListFromRealtimeSignal,
    markRead,
    markManyRead,
    remove,
    clearAll,
    openDrawer,
    closeDrawer,
    clearLocal,
    setToastsMuted,
  }
})
